/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Box, Button, Typography } from '@mui/material';

import { DefaultLanguage } from 'shared';

import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserUserApiService } from '../../services/SuperuserUserApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { UpdateSystemConfigurationRequest } from '../../types/superuser/UpdateSystemConfigurationRequest';
import { fetchSystemConfiguration } from '../../utils/queryFns';
import { useApiValidation } from '../../utils/useApiValidation';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { SystemConfigurationForm } from './components/SystemConfigurationForm/SystemConfigurationForm';
import { getInitialValues, systemConfigValidationSchema } from './SystemConfigurationPage.consts';
import { useSystemConfigurationPageStyles } from './SystemConfigurationPage.styles';
import { SystemConfigurationFormValues } from './SystemConfigurationPage.types';
import { SystemConfigurationPlaceholder } from './SystemConfigurationPlaceholder';

export const SystemConfigurationPage = memo(() => {
  const { t } = useTranslation(Namespaces.Admin);
  const classes = useSystemConfigurationPageStyles();
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.system_configuration`));
  const { hasFieldApiError, getFieldApiError, resetApiError, resetFieldApiError, setApiError } = useApiValidation();
  const [errorModalState, setErrorModalState] = useState({
    isOpen: false,
    error: {},
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState<boolean>(false);

  const systemConfigQuery = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn);

  const systemConfig = useMemo(() => systemConfigQuery.data?.payload, [systemConfigQuery.data]);

  useEffect(() => {
    if (systemConfigQuery.error) {
      const error = systemConfigQuery.error as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
    }
  }, [systemConfigQuery.error, setApiError]);

  const initialValues = useMemo(() => {
    if (systemConfig) {
      return getInitialValues({
        ...systemConfig,
        smtpHost: systemConfig.smtpHost ?? '',
        smtpPort: `${systemConfig.smtpPort ?? ''}`,
        smtpSecure: systemConfig.smtpSecure ?? false,
        smtpUser: systemConfig.smtpUser ?? '',
        smtpPass: systemConfig.smtpPass ?? '',
        enquiryEmailLanguage: systemConfig?.enquiryEmailLanguage || DefaultLanguage,
        smtpFromEmail: systemConfig.smtpFromEmail ?? null,
        minCpuLimit: systemConfig.minCpuLimit ?? undefined,
        maxCpuLimit: systemConfig.maxCpuLimit ?? undefined,
        minMemoryLimit: systemConfig.minMemoryLimit ?? null,
        maxMemoryLimit: systemConfig.maxMemoryLimit ?? null,
        minStorageLimit: systemConfig.minStorageLimit ?? null,
        maxStorageLimit: systemConfig.maxStorageLimit ?? null,
        emailAddress: systemConfig.emailAddress ?? null,
        emailToken: systemConfig.emailToken ?? null,
        showCost: systemConfig.showInstanceCost || systemConfig.showSupportCost,
      });
    }
    return getInitialValues();
  }, [systemConfig]);

  const submit = async (values: SystemConfigurationFormValues) => {
    resetApiError();
    setIsFetching(true);
    try {
      const data: UpdateSystemConfigurationRequest = {
        smtpHost: values.smtpHost?.trim() || null,
        smtpPort: Number(values.smtpPort) || null,
        smtpSecure: values.useSmtp ? values.smtpSecure : null,
        smtpUser: values.smtpUser?.trim() || null,
        smtpPass: values.smtpPass?.trim() || null,
        enquiryEmails: values.enquiryEmails.trim(),
        enquiryEmailLanguage: values.enquiryEmailLanguage,
        allowServiceCreation: values.allowServiceCreation,
        showInstanceCost: values.showInstanceCost,
        showSupportCost: values.showSupportCost,
        minCpuLimit: Number(values.minCpuLimit) || null,
        maxCpuLimit: Number(values.maxCpuLimit) || null,
        minMemoryLimit: Number(values.minMemoryLimit) || null,
        maxMemoryLimit: Number(values.maxMemoryLimit) || null,
        minStorageLimit: Number(values.minStorageLimit) || null,
        maxStorageLimit: Number(values.maxStorageLimit) || null,
        minReplicasLimit: Number(values.minReplicasLimit),
        maxReplicasLimit: Number(values.maxReplicasLimit),
        currency: values.currency.trim(),
        cpuPricePerMonth: Number(values.cpuPricePerMonth) || 0,
        memoryPricePerMonth: Number(values.memoryPricePerMonth) || 0,
        storagePricePerMonth: Number(values.storagePricePerMonth) || 0,
        cpuRequestToLimitMultiplier: Number(values.cpuRequestToLimitMultiplier) || 1,
        smtpFromEmail: values.smtpFromEmail,
        emailAddress: values.emailAddress,
        emailToken: values.emailToken,
        imprint: values.imprint,
      };
      await SuperuserUserApiService.updateSystemConfiguration({ data });
      setIsOpenSuccessModal(true);
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        const jsonObject = JSON.stringify(error.response.data, null, 2);
        setApiError(error);
        setErrorModalState({
          isOpen: true,
          error: jsonObject,
        });
      }
      console.log(error);
    } finally {
      setIsFetching(false);
      await systemConfigQuery.refetch();
    }
  };

  return (
    <div className={classes.root}>
      {systemConfigQuery.isLoading ? (
        <SystemConfigurationPlaceholder inputs={4} />
      ) : (
        <Box display="flex" flexDirection="column" width="100%" gap="24px">
          <Formik<SystemConfigurationFormValues>
            enableReinitialize
            onSubmit={submit}
            initialValues={initialValues}
            validationSchema={systemConfigValidationSchema}
            validateOnChange
            validateOnBlur
          >
            {formikProps => (
              <Box>
                <Typography variant="h2" className={classes.title}>
                  {t('system_configuration.title')}
                </Typography>
                <form onSubmit={formikProps.handleSubmit}>
                  <SystemConfigurationForm
                    {...formikProps}
                    isFetching={isFetching}
                    hasFieldApiError={hasFieldApiError}
                    getFieldApiError={getFieldApiError}
                    resetFieldApiError={resetFieldApiError}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    className={classes.submitBtn}
                    disabled={isFetching || !formikProps.dirty}
                    data-cy="system-config-submit-button"
                  >
                    {t('buttons.submit')}
                  </Button>
                </form>
                <SimpleNotifyModal
                  open={errorModalState.isOpen}
                  message={
                    errorModalState.error ? (
                      <pre className={classes.modalError}>{errorModalState.error.toString()}</pre>
                    ) : (
                      t(`${Namespaces.Common}:errors.unexpected_error`)
                    )
                  }
                  onOk={() => setErrorModalState({ isOpen: false, error: '' })}
                />
                <SimpleNotifyModal
                  open={isOpenSuccessModal}
                  message={t('system_configuration.change_saved')}
                  onOk={() => setIsOpenSuccessModal(false)}
                />
              </Box>
            )}
          </Formik>
        </Box>
      )}
    </div>
  );
});
